<script>
import {
  iIcon,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iSpinner,
  iBadge,
  iDrawer,
  iMenu,
  iMenuItem,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import CustomerFraudDocuments from '@/V2/customers/fraud/CustomerFraudDocuments.vue';
import { mapActions, mapGetters } from 'vuex';
import CustomerFraudSignalsDataBlock from '@/V2/customers/fraud/CustomerFraudSignalsDataBlock.vue';

export default {
  components: {
    iBadge,
    iButton,
    iDrawer,
    iIcon,
    iSpinner,
    iMenu,
    iMenuItem,
    CustomerFraudDocuments,
    CustomerFraudSignalsDataBlock,
  },
  data() {
    return {
      showSources: false,
      currentCustomerId: this.$route.params.customer_id,
      palette,
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
      isLoading: false,
      fetchError: null,
      selectedFinding: null,
    };
  },
  computed: {
    ...mapGetters(['getCachedFraudReport']),
    fetchedReportData() {
      return this.getCachedFraudReport(this.currentCustomerId);
    },
    displayOverview() {
      return this.fetchedReportData?.overview;
    },
    displayHighestRiskLevel() {
      return this.fetchedReportData?.risk_level;
    },
    displayFindings() {
      return this.fetchedReportData?.findings;
    },
    displayRecommendations() {
      return this.fetchedReportData?.recommended_actions;
    },
  },
  async mounted() {
    this.triggerFetchReport();
  },
  methods: {
    ...mapActions(['fetchAndCacheFraudReport']),
    async triggerFetchReport() {
      this.isLoading = true;
      this.fetchError = null;
      try {
        await this.fetchAndCacheFraudReport({
          customerId: this.currentCustomerId,
        });
      } catch (error) {
        this.fetchError = 'Failed to load fraud report. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },
    onShowSourcesClick(finding) {
      this.showSources = true;
      this.selectedFinding = finding;
    },
  },
};
</script>

<template>
  <div class="customer-fraud-report">
    <div class="i-p-11">
      <div class="d-flex flex-column i-gap-10">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center i-gap-6">
            <router-link
              :to="{
                name: 'customer-fraud-analyst',
                params: { customer_id: $route.params.customer_id },
              }"
            >
              <i-button
                data-testid="fraud-report-back-button"
                left-icon="medium-arrows-left"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.GHOST"
              />
            </router-link>
            <div class="d-flex flex-column">
              <span class="i-text-heading-5">Fraud Report</span>
              <span class="i-text-body-2 i-mt-1 i-text-neutral-110"
                >Detailed fraud analysis report for this customer</span
              >
            </div>
          </div>
        </div>

        <div v-if="isLoading" class="d-flex justify-content-center i-my-10">
          <i-spinner size="lg" />
        </div>

        <div v-else-if="fetchError" class="i-alert i-alert--danger i-p-6">
          {{ fetchError }}
        </div>

        <div v-else class="report-container">
          <div class="report-content i-py-8 d-flex flex-column i-gap-8">
            <h4 class="section-title i-mt-8">Overview</h4>
            <p class="section-body">
              {{ displayOverview }}
              <customer-fraud-documents
                :risk-level="displayHighestRiskLevel"
                class="i-mt-10"
              />
            </p>

            <div v-if="displayFindings && displayFindings.length">
              <h4 class="section-title i-mt-8">Findings</h4>
              <div class="section-body d-flex flex-column i-gap-4">
                <div
                  v-for="(finding, index) in displayFindings"
                  :key="'finding-' + index"
                  class="i-mb-4"
                >
                  <h5 class="finding-title">{{ finding.title }}</h5>
                  <p
                    v-for="(paragraph, pIndex) in finding.paragraphs"
                    :key="'finding-' + index + '-para-' + pIndex"
                    class="finding-paragraph"
                  >
                    {{ paragraph }}
                  </p>
                  <i-button
                    label="Sources"
                    :size="iButtonSizes.XSMALL"
                    :button-style="iButtonStyles.SOLID_LIGHT"
                    :type="iButtonTypes.NEUTRAL"
                    @button-click="onShowSourcesClick(finding)"
                  ></i-button>
                </div>
              </div>
              <i-drawer
                :visible="showSources"
                class="collect-session-modal"
                title="Sources"
                @hide-drawer="showSources = false"
              >
                <customer-fraud-signals-data-block
                  title="Source Documents"
                  :data="selectedFinding.urls"
                  type="documents"
                />
              </i-drawer>
            </div>

            <h4 class="section-title i-mt-8">Recommended Actions</h4>
            <p
              v-for="(recommendation, index) in displayRecommendations"
              :key="'finding-' + index"
              class="i-mb-4"
            >
              {{ recommendation }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.customer-fraud-report {
  overflow-y: auto;
}

.report-container {
  max-width: 960px;
  margin: 0 auto;
}

.report-list {
  margin: 0;
  list-style-position: inside;
}

.section-title {
  font-weight: 600;
  margin-bottom: 0px;
}

.section-body {
  font-size: 16px;
  color: #555;
  line-height: 1.5;
}

.finding-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
  margin-top: 15px;
}

.finding-paragraph {
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
}
</style>
